import { StyleSheet, View, ViewStyle } from 'react-native'
import Animated, { interpolate, SharedValue, useAnimatedStyle, withTiming } from 'react-native-reanimated'
import { ReactNode } from 'react'

export type FlipCardProps = {
  isFlipped: SharedValue<boolean>
  cardStyle?: ViewStyle
  containerStyle?: ViewStyle
  direction?: 'y' | 'x'
  duration?: number
  RegularContent: ReactNode
  FlippedContent: ReactNode
}

/**
 * A "card" that flips in a direction
 * Example: 
  <FlipCard
    isFlipped={isFlipped}
    cardStyle={{
      backfaceVisibility: 'hidden'
    }}
  />
*/
export const FlipCard = ({
  isFlipped,
  cardStyle,
  containerStyle,
  direction = 'y',
  duration = 350,
  RegularContent,
  FlippedContent
}: FlipCardProps) => {
  const isDirectionX = direction === 'x'

  const regularCardAnimatedStyle = useAnimatedStyle(() => {
    const spinValue = interpolate(Number(isFlipped.value), [0, 1], [0, 180])
    const rotateValue = withTiming(`${spinValue}deg`, { duration })

    return {
      transform: [isDirectionX ? { rotateX: rotateValue } : { rotateY: rotateValue }]
    }
  })

  const flippedCardAnimatedStyle = useAnimatedStyle(() => {
    const spinValue = interpolate(Number(isFlipped.value), [0, 1], [180, 360])
    const rotateValue = withTiming(`${spinValue}deg`, { duration })

    return {
      transform: [isDirectionX ? { rotateX: rotateValue } : { rotateY: rotateValue }]
    }
  })

  return (
    <View style={containerStyle} removeClippedSubviews={false} collapsable={false}>
      <Animated.View style={[flipCardStyles.regularCard, cardStyle, regularCardAnimatedStyle]}>
        {RegularContent}
      </Animated.View>
      <Animated.View style={[flipCardStyles.flippedCard, cardStyle, flippedCardAnimatedStyle]}>
        {FlippedContent}
      </Animated.View>
    </View>
  )
}

const flipCardStyles = StyleSheet.create({
  regularCard: {
    position: 'absolute',
    zIndex: 1
  },
  flippedCard: {
    zIndex: 2
  }
})
